import { useCallback, useEffect, useRef } from 'react';
import { useIFCStore, useGlobalStore, useIFCFineTuningStore } from '../stores';
import { useUIStore } from '../stores';
import { useUnifiedElementsStore } from '../stores/unifiedElementsStore';
import IFCService from '../services/ifc/ifcService';
import { logger } from '../../../shared/utils/logger';
import { createIFCFingerprint } from '../utils/ifcFingerprint';
import { useGlobalStore } from '../stores';

export const useIFC = (mapService?: any) => {
  const { 
    currentModel, 
    addModel, 
    setCurrentStorey, 
    addSelectedCategory, 
    removeSelectedCategory,
    setSelectedCategories,
    selectedCategories,
    filteredCategories,
    currentStorey,
    updateAvailableCategoriesForStorey,
    setLoading: setIFCLoading,
    setError: setIFCError
  } = useIFCStore();
  
  // Usar ref para acceder al valor actual de selectedCategories sin causar re-renders
  const selectedCategoriesRef = useRef(selectedCategories);
  selectedCategoriesRef.current = selectedCategories;

  const { addNotification } = useUIStore();
  const { setSelectedStoreyId } = useGlobalStore();
  const ifcService = IFCService.getInstance();

  // Efecto para monitorear cambios en filteredCategories
  useEffect(() => {
    logger.log('USE_IFC', '🔄 useIFC: filteredCategories cambiaron:', filteredCategories);
  }, [filteredCategories]);

  // Efecto para monitorear cambios en selectedCategories
  useEffect(() => {
    logger.log('USE_IFC', '🔄 useIFC: selectedCategories cambiaron:', selectedCategories);
  }, [selectedCategories]);

  const loadIFCModel = useCallback(async (file: File) => {
    setIFCLoading(true);
    setIFCError(null);

    try {
      logger.log('USE_IFC', '🚀 Iniciando carga de IFC...');
      logger.log('USE_IFC', '🔍 mapService recibido:', mapService);
      
      // Obtener el mapa del mapService
      let map = null;
      if (mapService) {
        logger.log('USE_IFC', '✅ mapService existe');
        if (mapService.getMap) {
          logger.log('USE_IFC', '✅ mapService tiene método getMap');
          map = mapService.getMap();
          logger.log('USE_IFC', '🗺️ Mapa obtenido:', map);
        } else {
          logger.error('USE_IFC', '❌ mapService no tiene método getMap');
        }
      } else {
        logger.error('USE_IFC', '❌ mapService es null o undefined');
      }
      
      if (!map) {
        throw new Error('No se pudo obtener el mapa del mapService. Verifica que el mapa esté inicializado.');
      }
      
      // Usar IFCService para cargar el modelo
      const geometryData = await ifcService.loadModel(file, map, mapService);
      
      // Obtener metadatos del IFC
      const ifcModelInstance = geometryData.ifcModelInstance;
      let ifcName = '';
      let numFloors = 0;
      
      try {
        ifcName = ifcModelInstance.getIfcmetadataName() || '';
        numFloors = ifcModelInstance.getNumStoreys() || 0;
        logger.log('USE_IFC', '📊 Metadatos IFC:', { ifcName, numFloors });
      } catch (error) {
        logger.warn('USE_IFC', 'No se pudieron obtener los metadatos del IFC:', error);
      }

      const fingerprint = createIFCFingerprint({ file, modelName: ifcName || file.name });
      const fineTuningStore = useIFCFineTuningStore.getState();

      if (fingerprint) {
        fineTuningStore.registerModel(fingerprint, ifcName || file.name);
        const persistedOffsets = fineTuningStore.getOffsets(fingerprint);

        if (
          persistedOffsets &&
          (persistedOffsets.offsetX !== 0 ||
            persistedOffsets.offsetY !== 0 ||
            persistedOffsets.rotation !== 0)
        ) {
          logger.log('USE_IFC', '♻️ Restaurando ajustes finos guardados:', persistedOffsets);
          ifcModelInstance.updateFineTuning(
            persistedOffsets.offsetX,
            persistedOffsets.offsetY,
            persistedOffsets.rotation
          );
        }
      } else {
        fineTuningStore.reset();
      }

      // Mostrar información detallada de todos los niveles y sus categorías
      logger.log('USE_IFC', '🏢 === INFORMACIÓN DETALLADA DE NIVELES Y CATEGORÍAS ===');
      if (geometryData.storeys.length > 0) {
        logger.log('USE_IFC', `📋 Total de niveles encontrados: ${geometryData.storeys.length}`);
        
        geometryData.storeys.forEach((storey: any, index: number) => {
          const storeyId = String(storey.expressID);
          const storeyName = storey.Name?.value || `Nivel ${index + 1}`;
          
          logger.log('USE_IFC', `\n🏗️  Nivel ${index + 1}: ${storeyName}`);
          logger.log('USE_IFC', `   ID: ${storeyId}`);
          
                     // Obtener categorías específicas de este nivel usando getCategoryCountsForStorey
           const categoryCountsForThisStorey = ifcService.getCategoryCountsForStorey(storeyId);
           const categoriesForThisStorey = Object.keys(categoryCountsForThisStorey);
           logger.log('USE_IFC', `   📦 Categorías (${categoriesForThisStorey.length}):`);
           
                        if (categoriesForThisStorey.length > 0) {
               categoriesForThisStorey.forEach((category: string, catIndex: number) => {
                 const categoryName = ifcService.getCurrentIfcModel()?.getCategoryNamefromCategoryID(category) || category;
                 const count = categoryCountsForThisStorey[category];
                 logger.log('USE_IFC', `      ${catIndex + 1}. ${categoryName} (${category}) - ${count} elementos`);
               });
             } else {
               logger.log('USE_IFC', `      ⚠️  No se encontraron categorías para este nivel`);
             }
        });
        
              logger.log('USE_IFC', '\n🎯 === RESUMEN ===');
      logger.log('USE_IFC', `📊 Total de niveles: ${geometryData.storeys.length}`);
      logger.log('USE_IFC', `📦 Total de categorías únicas en todo el modelo: ${geometryData.categories.length}`);
        
        // Mostrar todas las categorías únicas del modelo
        logger.log('USE_IFC', '\n📋 Todas las categorías únicas del modelo:');
        geometryData.categories.forEach((category: string, index: number) => {
          const categoryName = ifcService.getCurrentIfcModel()?.getCategoryNamefromCategoryID(category) || category;
          logger.log('USE_IFC', `   ${index + 1}. ${categoryName} (${category})`);
        });
        
      } else {
        logger.log('USE_IFC', '⚠️  No se encontraron niveles en el modelo IFC');
        logger.log('USE_IFC', `📦 Categorías totales: ${geometryData.categories.length}`);
      }
      logger.log('USE_IFC', '🏢 === FIN INFORMACIÓN DETALLADA ===\n');

      // Seleccionar y dibujar automáticamente la primera planta usando el desplegable del GUI (GUID)
      if (geometryData.storeys.length > 0) {
        const gui = mapService?.getGUI ? mapService.getGUI() : null;
        const storeyOptions = gui?.getStoreyDropdownOptions ? gui.getStoreyDropdownOptions() : [];
        if (storeyOptions && storeyOptions.length > 0) {
          const firstOption = storeyOptions[0];
          const firstStoreyId = String(firstOption.id);
          logger.log('USE_IFC', '🎯 Seleccionando primera planta (GUI/ GUID):', firstStoreyId, firstOption.name);

          // Actualizar store global y local
          setSelectedStoreyId(firstStoreyId);

          // Establecer currentStorey buscando por GUID
          const storeyObj = geometryData.storeys.find((s: any) => String(s.GlobalId?.value) === firstStoreyId || String(s.expressID) === firstStoreyId);
          if (storeyObj) setCurrentStorey(storeyObj);

          // Calcular categorías disponibles para esta planta y seleccionarlas
          const categoryCounts = ifcService.getCategoryCountsForStorey(firstStoreyId);
          const availableForFirst = Object.keys(categoryCounts);
          updateAvailableCategoriesForStorey(availableForFirst);
          setSelectedCategories(availableForFirst);

          // Dibujar y notificar
          ifcService.drawStorey(firstStoreyId);
          if (gui) gui.selectStoreyById(firstStoreyId);
          logger.log('USE_IFC', '✅ Primera planta dibujada y activada en mapa');
        } else {
          // Fallback a la lógica previa con el primer storey por orden
          const firstStorey = geometryData.storeys[0];
          const fallbackId = String(firstStorey.GlobalId?.value ?? firstStorey.expressID);
          setCurrentStorey(firstStorey);
          setSelectedStoreyId(fallbackId);
          const counts = ifcService.getCategoryCountsForStorey(fallbackId);
          const available = Object.keys(counts);
          updateAvailableCategoriesForStorey(available);
          setSelectedCategories(available);
          ifcService.drawStorey(fallbackId);
          logger.log('USE_IFC', '✅ Primera planta dibujada (fallback)');
        }
      } else {
        // Si no hay plantas, usar todas las categorías disponibles
        const allCategories = geometryData.categories;
        logger.log('USE_IFC', '🎨 No hay plantas, usando todas las categorías:', allCategories);
        updateAvailableCategoriesForStorey(allCategories);
        
                 // Seleccionar todas las categorías
         setSelectedCategories(allCategories);
      }

      await (async () => {
        try {
          const coordinates = ifcModelInstance.getIFCCoordinates();
          if (!coordinates) {
            logger.warn('USE_IFC', '⚠️ No se pudieron leer lat/lon del IFC; elevación inicial no calculada');
            return;
          }

          const [adjustOffsetX, adjustOffsetZ] = ifcModelInstance.getAdjustOffset();
          const hasFineTuning = Math.abs(adjustOffsetX) >= 0.1 || Math.abs(adjustOffsetZ) >= 0.1;

          let ellipsoidalElevation: number | null = null;

          if (hasFineTuning) {
            ellipsoidalElevation = await ifcModelInstance.calculateEllipsoidalElevationWithOffsets(true);
          } else {
            ellipsoidalElevation = await ifcModelInstance.calculateEllipsoidalElevationFromCoordinates(
              coordinates.lat,
              coordinates.lon,
              true
            );
          }

          if (!ellipsoidalElevation || ellipsoidalElevation <= 0) {
            logger.warn('USE_IFC', '⚠️ Cálculo de elevación inicial devolvió un valor inválido');
            return;
          }

          logger.log('USE_IFC', `🧮 Elevación inicial estimada (elipsoidal): ${ellipsoidalElevation.toFixed(2)} m`);
          const { setElevation, setElevationType } = useGlobalStore.getState();
          setElevation(ellipsoidalElevation);
          setElevationType('ellipsoidal');
          ifcModelInstance.setElevation(ellipsoidalElevation, 'ellipsoidal');
        } catch (error) {
          logger.error('USE_IFC', '❌ Error al calcular la elevación inicial del IFC:', error);
        }
      })();

      // Actualizar el store de IFC
      const model = {
        id: geometryData.modelId,
        name: ifcName || file.name,
        file: file,
        fingerprint: fingerprint,
        storeys: geometryData.storeys,
        categories: geometryData.categories,
        isLoaded: true,
        center: geometryData.center,
        extent: geometryData.extent,
        // Añadir referencia a la instancia real del modelo
        ifcModelInstance: ifcModelInstance,
      };
      
      addModel(model);
      
      // Migrar elementos existentes de expressID a GUID
      setTimeout(() => {
        const unifiedStore = useUnifiedElementsStore.getState();
        unifiedStore.migrateStoreyIdsToGuids();
      }, 1000); // Dar tiempo para que se cargue el modelo
      
      // Centrar el IFC en el mapa antes de cerrar el modal
      setTimeout(() => {
        if (ifcModelInstance && ifcModelInstance.centerOnMap) {
          const centered = ifcModelInstance.centerOnMap();
          if (centered) {
            logger.log('USE_IFC', '🎯 IFC centrado en el mapa');
            // Cerrar el modal de carga después de centrar
            setIFCLoading(false);
          } else {
            logger.warn('USE_IFC', '⚠️ No se pudo centrar el IFC, cerrando modal de todas formas');
            setIFCLoading(false);
          }
        } else {
          logger.warn('USE_IFC', '⚠️ No se pudo acceder al método centerOnMap, cerrando modal');
          setIFCLoading(false);
        }
      }, 500); // Dar tiempo para que se renderice el modelo
      
      addNotification({
        type: 'success',
        title: 'IFC Cargado',
        message: `Modelo "${ifcName || file.name}" cargado correctamente con ${numFloors} plantas`
      });

      return { success: true, model };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      logger.error('USE_IFC', '❌ Error completo:', error);
      setIFCError(errorMessage);
      addNotification({
        type: 'error',
        title: 'Error al cargar IFC',
        message: errorMessage
      });
      return { success: false, error: errorMessage };
    } finally {
      // En caso de error, cerrar el modal inmediatamente
      setIFCLoading(false);
    }
     }, [ifcService, mapService, setIFCLoading, setIFCError, setCurrentStorey, addModel, addNotification, selectedCategories, setSelectedCategories, updateAvailableCategoriesForStorey]);

  const changeStorey = useCallback((storeyId: string) => {
    logger.log('USE_IFC', '🔄 changeStorey llamado con storeyId:', storeyId);
    logger.log('USE_IFC', '🔍 Estado actual:', {
      currentModel: !!currentModel,
      currentStoreyId: currentStorey?.expressID ?? currentStorey?.GlobalId?.value,
      selectedCategories,
      filteredCategories
    });

    // storeyId ahora es un GUID, buscar por GUID primero, luego por expressID como fallback
    const storey = currentModel?.storeys?.find((s: any) => 
      String(s.GlobalId?.value) === String(storeyId) || String(s.expressID) === String(storeyId)
    );
    if (storey) {
      logger.log('USE_IFC', '✅ Planta encontrada:', storey);
      setCurrentStorey(storey);
      
      // Solo obtener categorías si el modelo está cargado
      if (ifcService.getCurrentIfcModel()) {
        // Obtener categorías disponibles para esta planta específica usando getCategoryCountsForStorey
        const categoryCountsForStorey = ifcService.getCategoryCountsForStorey(storeyId);
        const availableCategoriesForStorey = Object.keys(categoryCountsForStorey);
       logger.log('USE_IFC', '🎨 Categorías disponibles para la planta', storeyId, ':', availableCategoriesForStorey);
       logger.log('USE_IFC', '📊 Conteo de elementos por categoría:', categoryCountsForStorey);
      
        // Mostrar información detallada de las categorías del nuevo nivel
        logger.log('USE_IFC', `\n🏗️  === CAMBIO A NUEVO NIVEL ===`);
        logger.log('USE_IFC', `📋 Nivel: ${storey.Name?.value || storeyId}`);
        logger.log('USE_IFC', `🆔 ID: ${storeyId}`);
        logger.log('USE_IFC', `📦 Categorías encontradas (${availableCategoriesForStorey.length}):`);
        
        if (availableCategoriesForStorey.length > 0) {
          availableCategoriesForStorey.forEach((category: string, index: number) => {
            const categoryName = ifcService.getCurrentIfcModel()?.getCategoryNamefromCategoryID(category) || category;
            const count = categoryCountsForStorey[category];
            logger.log('USE_IFC', `   ${index + 1}. ${categoryName} (${category}) - ${count} elementos`);
          });
        } else {
          logger.log('USE_IFC', `   ⚠️  No se encontraron categorías para este nivel`);
        }
        logger.log('USE_IFC', `🏗️  === FIN CAMBIO A NUEVO NIVEL ===\n`);
        
        // Actualizar las categorías disponibles en el store
        updateAvailableCategoriesForStorey(availableCategoriesForStorey);
        logger.log('USE_IFC', '✅ Categorías filtradas actualizadas en el store');
        
        // Limpiar categorías seleccionadas y seleccionar todas las de esta planta
        logger.log('USE_IFC', '🧹 Limpiando categorías seleccionadas actuales:', selectedCategoriesRef.current);
        setSelectedCategories([]); // Limpiar todas las categorías seleccionadas
        
        // Luego agregamos todas las categorías de la nueva planta
        logger.log('USE_IFC', '➕ Agregando categorías de la nueva planta:', availableCategoriesForStorey);
        setSelectedCategories(availableCategoriesForStorey); // Establecer directamente las nuevas categorías
        
        ifcService.drawStorey(storeyId);
        
        addNotification({
          type: 'info',
          title: 'Planta cambiada',
          message: `Visualizando planta seleccionada con ${availableCategoriesForStorey.length} categorías`
        });
      } else {
        // Si no hay modelo cargado, solo actualizar el store global
        logger.log('USE_IFC', '⚠️ Modelo IFC no cargado, solo actualizando store global');
      }
      
      // Actualizar el store global (siempre se ejecuta)
      setSelectedStoreyId(storeyId);
      
      return true;
    } else {
      console.error('❌ Planta no encontrada:', storeyId);
      return false;
    }
  }, [currentModel, setCurrentStorey, ifcService, setSelectedCategories, updateAvailableCategoriesForStorey, addNotification]);

  const toggleCategory = useCallback((category: string) => {
    if (selectedCategories.includes(category)) {
      removeSelectedCategory(category);
      const newCategories = selectedCategories.filter(c => c !== category);
      ifcService.filterCategories(newCategories);
    } else {
      addSelectedCategory(category);
      const newCategories = [...selectedCategories, category];
      ifcService.filterCategories(newCategories);
    }
  }, [selectedCategories, removeSelectedCategory, addSelectedCategory, ifcService]);

  const getCategoryName = useCallback((category: string) => {
    const ifcModel = ifcService.getCurrentIfcModel();
    logger.log('USE_IFC', `🔍 getCategoryName: category=${category}, ifcModel=${!!ifcModel}`);
    
    if (!ifcModel) {
      logger.warn('USE_IFC', `⚠️ No hay modelo IFC disponible para obtener nombre de categoría: ${category}`);
      return category;
    }
    
    const categoryName = ifcModel.getCategoryNamefromCategoryID(category);
    logger.log('USE_IFC', `📋 getCategoryName: ${category} → ${categoryName}`);
    return categoryName;
  }, [ifcService]);

  const getAvailableCategoriesForStorey = useCallback((storeyId: string) => {
    return ifcService.getAvailableCategoriesForStorey(storeyId);
  }, [ifcService]);

  return {
    // Estado
    currentModel,
    selectedCategories,
    currentStorey,
    selectedFile: currentModel?.file || null,
    availableStoreys: currentModel?.storeys || [],
    availableCategories: filteredCategories, // Usar categorías filtradas por planta
    // Usar GUID como identificador principal para coherencia con MapService y IFCService
    currentStoreyId: String((currentStorey?.GlobalId?.value ?? currentStorey?.expressID) || ''),
    
    // Debug info
    debug: {
      filteredCategories,
      selectedCategories,
      currentStorey,
      hasCurrentModel: !!currentModel,
    },
    
    // Acciones
    loadIFCModel,
    changeStorey,
    toggleCategory,
    getCategoryName,
    getAvailableCategoriesForStorey,
  };
};
